import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { BaseSectionType, InteractionCounts } from "./types";
import { Box, Flex, Fade, Tabs, TabList, Tab, TabIndicator, Text, CircularProgress, Alert, AlertIcon, Button, Collapse, VStack } from "@chakra-ui/react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { defaultSchema } from 'hast-util-sanitize';
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import MeetingHeaderDetails from "./MeetingHeaderDetails";
import { useMeetingContext } from "./MeetingContext";
import ExpandableSection from "./ExpandableSection";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { conversationService } from "../../services/conversationService";
import { appAnalyticsService } from "../../services/appAnalyticsService";
import { meetingService, type Meeting as MeetingFromService } from "../../services/meetingService";
import html2pdf from 'html2pdf.js';
import { marked } from 'marked';
import { cleanContentErrors, cleanApiErrorMessage } from "../../utils/errorUtils";

marked.setOptions({ gfm: true });

dayjs.extend(utc);
dayjs.extend(timezone);

// Custom sanitize schema to allow highlighting styles
const customSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span || []), 'style']
  }
};

// --- Locally Defined Types ---
interface GeminiContent {
  content?: string; // Optional markdown content string
  summary?: {
    sections: {
      strategic_summary?: string;
      meeting_summary?: string;
      key_topics?: string;
      action_items?: string;
      participants?: string;
      decisions_made?: string;
      questions_raised?: string;
      client_snapshot?: string;
      key_drivers_risk?: string;
      product_options?: string;
      emotional_arc?: string;
    }
  };
  journey?: Journey;
}
interface Interaction {
  type: string;
  startTime: string;
  endTime: string;
  timeSpent: number;
  status: string;
}

interface Session {
  sessionId: string;
  customerId: string;
  startTime: string;
  endTime: string;
  interactions: Interaction[];
}

interface Journey {
  overview: {
    totalInteractions: number;
    firstInteraction: string | null;
    lastInteraction: string | null;
    successRate: number;
    interactionTypes: string[];
  };
  sessions: Session[];
}
interface MissedInteractionsData {
  data: string[];
  success: boolean;
}

interface CombinedInteractions {
  allInteractions: GeminiContent | { error: string };
  missedInteractions: MissedInteractionsData | { error: string };
}
interface Meeting {
  meeting_id: string;
  clientId?: string;
  conversation_id?: string | string[] | null;
  request_id?: string;
}

interface ApiError {
  message: string;
  section: BaseSectionType;
}

interface ContentSegment {
  type: 'content' | 'quote' | 'subsection';
  content: string;
  subsectionTitle?: string;
}

interface ParsedContent {
  segments: ContentSegment[];
}

// CollapsibleQuote component for handling supporting quotes and subsections
const CollapsibleQuote: React.FC<{ quote: string; index: number; title?: string }> = ({ quote, index, title }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!quote.trim()) return null;

  return (
    <Box className="chakra-collapse" mt={4} mb={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
      <Button
        variant="ghost"
        width="100%"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        px={4}
        py={3}
        fontWeight="medium"
        fontSize="sm"
        color="gray.700"
        _hover={{ bg: "gray.100" }}
      >
        <Text>{title || `Supporting Statement ${index + 1}`}</Text>
        {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Button>
      <Collapse in={isOpen}>
        <Box p={4} pt={0}>
          <Box
            p={3}
            bg="white"
            borderRadius="md"
            borderLeft="4px solid"
            borderLeftColor="blue.300"
            fontSize="sm"
            lineHeight="1.6"
            className="collapsible-quote-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizeSchema]]}
            >
              {quote}
            </ReactMarkdown>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// Function to parse markdown content and extract supporting quotes and subsections inline
const parseContentAndQuotes = (content: string, sectionName?: string): ParsedContent => {
  if (!content || content === "Loading..." || content.startsWith("Error loading") || content.startsWith("No data available")) {
    return { segments: [{ type: 'content', content }] };
  }

  let lines = content.split('\n');

  // Remove the section heading if it matches the section name (to avoid duplication)
  if (sectionName && lines.length > 0) {
    const firstLine = lines[0].trim();

    // Normalize section name for comparison (handle possessives, apostrophes, extra spaces, etc.)
    const normalizedSectionName = sectionName.toLowerCase()
      .replace(/'s\b/g, '') // Remove possessive 's (e.g., "Client's" → "Client")
      .replace(/['`]/g, '') // Remove remaining apostrophes
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();

    // Check if first line is a heading that matches the section name
    if (firstLine.startsWith('#')) {
      const headingText = firstLine.replace(/^#+\s*/, '').toLowerCase()
        .replace(/'s\b/g, '') // Remove possessive 's
        .replace(/['`]/g, '') // Remove remaining apostrophes  
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();

      if (headingText === normalizedSectionName) {
        lines = lines.slice(1); // Remove the first line
        // Also remove any empty lines that follow
        while (lines.length > 0 && lines[0].trim() === '') {
          lines = lines.slice(1);
        }
      }
    }
  }

  const segments: ContentSegment[] = [];
  let currentContentLines: string[] = [];
  let currentQuoteLines: string[] = [];
  let currentSubsectionLines: string[] = [];
  let inQuoteSection = false;
  let inSubsection = false;
  let currentSubsectionTitle: string | undefined = undefined;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const lowerLine = line.toLowerCase();

    // Check if this line starts a quote section
    if ((lowerLine.startsWith('supporting quote') || lowerLine.startsWith('quote') ||
      lowerLine.startsWith('## supporting quote') || lowerLine.startsWith('## quote') ||
      lowerLine.startsWith('### supporting quote') || lowerLine.startsWith('### quote') ||
      lowerLine.startsWith('supporting quotes') || lowerLine.includes('supporting quotes') || lowerLine.includes('quote')) &&
      sectionName !== "All Interactions") {

      // Save any accumulated content before this quote
      if (currentContentLines.length > 0) {
        segments.push({
          type: 'content',
          content: currentContentLines.join('\n').trim()
        });
        currentContentLines = [];
      }

      // If we were already in a quote section, save the previous quote
      if (inQuoteSection && currentQuoteLines.length > 0) {
        segments.push({
          type: 'quote',
          content: currentQuoteLines.join('\n').trim()
        });
        currentQuoteLines = [];
      }

      // Start new quote section
      inQuoteSection = true;
      continue; // Skip the header line
    }

    // Check for subsections in All Interactions
    else if (sectionName === "All Interactions" && (lowerLine.startsWith('## property details') || lowerLine.startsWith('## payment plans'))) {
      // Save any accumulated content or previous subsection
      if (currentContentLines.length > 0) {
        segments.push({
          type: 'content',
          content: currentContentLines.join('\n').trim()
        });
        currentContentLines = [];
      }
      if (inSubsection && currentSubsectionLines.length > 0) {
        segments.push({
          type: 'subsection',
          content: currentSubsectionLines.join('\n').trim(),
          subsectionTitle: currentSubsectionTitle
        });
        currentSubsectionLines = [];
      }

      // Start new subsection
      inSubsection = true;
      inQuoteSection = false;
      currentSubsectionTitle = lowerLine.includes('property details') ? 'Property Details' : 'Payment Plans';
      continue; // Skip the header line
    }

    // Check if we're starting a new section (and should end current quote or subsection)
    else if (line.startsWith('#') && (inQuoteSection || inSubsection)) {
      // Save current quote if it exists
      if (inQuoteSection && currentQuoteLines.length > 0) {
        segments.push({
          type: 'quote',
          content: currentQuoteLines.join('\n').trim()
        });
        currentQuoteLines = [];
      }
      // Save current subsection if it exists
      if (inSubsection && currentSubsectionLines.length > 0) {
        segments.push({
          type: 'subsection',
          content: currentSubsectionLines.join('\n').trim(),
          subsectionTitle: currentSubsectionTitle
        });
        currentSubsectionLines = [];
      }
      inQuoteSection = false;
      inSubsection = false;
      currentSubsectionTitle = undefined;
      currentContentLines.push(lines[i]);
    }

    // If we're in a quote section, add to current quote
    else if (inQuoteSection) {
      currentQuoteLines.push(lines[i]);
    }

    // If we're in a subsection, add to current subsection
    else if (inSubsection) {
      currentSubsectionLines.push(lines[i]);
    }

    // Otherwise, add to main content
    else {
      currentContentLines.push(lines[i]);
    }
  }

  // Handle any remaining content
  if (currentContentLines.length > 0) {
    segments.push({
      type: 'content',
      content: currentContentLines.join('\n').trim()
    });
  }

  // Handle the last quote if we were in a quote section
  if (inQuoteSection && currentQuoteLines.length > 0) {
    segments.push({
      type: 'quote',
      content: currentQuoteLines.join('\n').trim()
    });
  }

  // Handle the last subsection if we were in a subsection
  if (inSubsection && currentSubsectionLines.length > 0) {
    segments.push({
      type: 'subsection',
      content: currentSubsectionLines.join('\n').trim(),
      subsectionTitle: currentSubsectionTitle
    });
  }

  return { segments: segments.filter(segment => segment.content.length > 0) };
};

// Section name mapping
const sectionNameMapping: Record<BaseSectionType, string> = {
  "Strategic Summary": "Strategic Summary",
  "Meeting Summary": "Meeting Summary",
  "Key Topics": "Key Topics",
  "Action Items": "Action Items",
  "Participants": "Participants",
  "Decisions Made": "Decisions Made",
  "Questions Raised": "Questions Raised",
  "Client's Snapshot": "Client's Snapshot",
  "Key Drivers & Risks": "Key Drivers & Risks",
  "Product Options": "Product Options",
  "Emotional Arc & Sentiment": "Emotional Arc & Sentiment",
  "All Interactions": "All Interactions"
};

// Map UI section names to API response keys
const apiSectionKeyMap: Record<string, keyof NonNullable<NonNullable<GeminiContent['summary']>['sections']>> = {
  "Strategic Summary": "strategic_summary",
  "Meeting Summary": "meeting_summary",
  "Key Topics": "key_topics",
  "Action Items": "action_items",
  "Participants": "participants",
  "Decisions Made": "decisions_made",
  "Questions Raised": "questions_raised",
  "Client's Snapshot": "client_snapshot",
  "Key Drivers & Risks": "key_drivers_risk",
  "Product Options": "product_options",
  "Emotional Arc & Sentiment": "emotional_arc",
};

const PDF_SECTION_ORDER: BaseSectionType[] = [
  "Strategic Summary", "Meeting Summary", "Key Topics", "Action Items", "Participants",
  "Decisions Made", "Questions Raised", "Client's Snapshot", "Key Drivers & Risks",
  "Product Options", "Emotional Arc & Sentiment"
];

/** Format combined interactions data into markdown for the All Interactions PDF section. */
function formatAllInteractionsToMarkdown(data: CombinedInteractions | null): string {
  if (!data) return 'No interactions data available.';
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };
  const formatDateToIST = (isoDate: string): string => {
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Kolkata',
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).replace(',', '');
    } catch {
      return 'N/A';
    }
  };

  let allInteractions: Array<{ srNo: number; type: string; startTime: string; duration: string; status: string }> = [];
  const allInteractionsJourney = (data as CombinedInteractions).allInteractions && 'journey' in (data as CombinedInteractions).allInteractions ? (data as CombinedInteractions).allInteractions as GeminiContent : null;
  const journeySessions = allInteractionsJourney?.journey?.sessions;
  if (Array.isArray(journeySessions)) {
    allInteractions = journeySessions.flatMap((session: Session, sessionIndex: number) =>
      (session.interactions || []).map((item: Interaction, index: number) => ({
        srNo: sessionIndex * (session.interactions?.length || 0) + index + 1,
        type: item.type || 'Unknown',
        startTime: formatDateToIST(item.startTime || ''),
        duration: item.timeSpent != null ? formatDuration(item.timeSpent) : 'N/A',
        status: 'complete'
      }))
    );
  } else if ('journey' in data && (data as GeminiContent).journey && Array.isArray((data as GeminiContent).journey?.sessions)) {
    const sessions = (data as GeminiContent).journey!.sessions;
    allInteractions = sessions.flatMap((session: Session, sessionIndex: number) =>
      (session.interactions || []).map((item: Interaction, index: number) => ({
        srNo: sessionIndex * (session.interactions?.length || 0) + index + 1,
        type: item.type || 'Unknown',
        startTime: formatDateToIST(item.startTime || ''),
        duration: item.timeSpent != null ? formatDuration(item.timeSpent) : 'N/A',
        status: 'complete'
      }))
    );
  } else if ('allInteractions' in data && data.allInteractions && 'error' in data.allInteractions) {
    allInteractions.push({
      srNo: 1,
      type: 'Error',
      startTime: 'N/A',
      duration: 'N/A',
      status: cleanApiErrorMessage(String((data.allInteractions as { error: string }).error), 'Error loading interactions')
    });
  }

  let output = '';
  if (allInteractions.length > 0) {
    const tableHeader = `| Sr.No. | Type | Start Time | Duration | Status |\n|--------|------|------------|----------|--------|\n`;
    const tableRows = allInteractions.map((item, i) => `| ${i + 1} | ${item.type} | ${item.startTime} | ${item.duration} | ${item.status} |`).join('\n');
    output += `## Covered Interactions: ${allInteractions.length}\n\n${tableHeader}${tableRows}\n\n`;
  } else {
    output += `## Covered Interactions: 0\n\nNo interactions available.\n\n`;
  }

  if ('missedInteractions' in data && data.missedInteractions && 'data' in data.missedInteractions && Array.isArray(data.missedInteractions.data)) {
    const allTypes = new Set(allInteractions.map((item) => item.type.toUpperCase()));
    const missed = data.missedInteractions.data
      .filter((type: string) => !allTypes.has(type.toUpperCase()))
      .map((type: string, index: number) => ({ srNo: index + 1, type, startTime: '---', duration: '---', status: 'pending' }));
    if (missed.length > 0) {
      const tableHeader = `| Sr.No. | Type | Start Time | Duration | Status |\n|--------|------|------------|----------|--------|\n`;
      const tableRows = missed.map((item: { srNo: number; type: string; startTime: string; duration: string; status: string }, i: number) => `| ${item.srNo} | ${item.type} | ${item.startTime} | ${item.duration} | ${item.status} |`).join('\n');
      output += `## Missed Interactions: ${missed.length}\n\n${tableHeader}${tableRows}`;
    } else {
      output += `## Missed Interactions: 0\n\nNo interactions available.`;
    }
  } else if ('missedInteractions' in data && data.missedInteractions && 'error' in data.missedInteractions) {
    output += `## Missed Interactions: 0\n\n⚠️ Error loading missed interactions: ${cleanApiErrorMessage(String((data.missedInteractions as { error: string }).error), 'processing error')}.`;
  } else {
    output += `## Missed Interactions: 0\n\nNo interactions data available.`;
  }
  return output;
}

/** Build PDF container from API summary data (no DOM clone). Used for batch background uploads. Exported for BackgroundPdfUploadTrigger. */
export function buildPdfContainerFromSummaryData(
  meeting: { name?: string; date?: string; project?: string; client?: string },
  summaryData: GeminiContent | null,
  interactionsData?: CombinedInteractions | null
): HTMLElement {
  const container = document.createElement('div');
  container.style.width = '210mm';
  container.style.maxWidth = '210mm';
  container.style.margin = '0';
  container.style.padding = '20mm';
  container.style.boxSizing = 'border-box';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.4';
  container.style.color = '#333';
  container.style.backgroundColor = 'white';

  const header = document.createElement('div');
  header.style.textAlign = 'center';
  header.style.marginBottom = '30px';
  header.style.paddingBottom = '20px';
  header.style.borderBottom = '2px solid #e2e8f0';
  header.innerHTML = `
    <h1 style="margin: 0 0 10px 0; font-size: 20px; font-weight: bold; color: #2d3748;">
      Meeting: ${escapeHtml(meeting?.name || 'Untitled')}
    </h1>
    <p style="margin: 5px 0; font-size: 14px; color: #718096;">
      Date: ${escapeHtml(meeting?.date || 'N/A')}
    </p>
    <p style="margin: 5px 0; font-size: 14px; color: #718096;">
      Project: ${escapeHtml(meeting?.project || 'N/A')}
    </p>
  `;
  container.appendChild(header);

  const sections = summaryData?.summary?.sections;
  const raw = summaryData as unknown as { content?: string };
  if (sections) {
    for (const sectionName of PDF_SECTION_ORDER) {
      const key = apiSectionKeyMap[sectionName];
      const value = key ? (sections as Record<string, unknown>)[key] : null;
      const text = value != null ? String(value) : '';
      if (!text.trim()) continue;
      const sectionTitle = document.createElement('h2');
      sectionTitle.style.marginTop = '24px';
      sectionTitle.style.marginBottom = '12px';
      sectionTitle.style.lineHeight = '1.4';
      sectionTitle.textContent = sectionNameMapping[sectionName] ?? sectionName;
      container.appendChild(sectionTitle);
      const sectionContent = document.createElement('div');
      sectionContent.className = 'pdf-section-content';
      sectionContent.style.marginBottom = '16px';
      sectionContent.style.lineHeight = '1.8';
      sectionContent.innerHTML = markdownToHtml(text);
      container.appendChild(sectionContent);
    }
  }
  if (!sections && raw?.content?.trim()) {
    const sectionTitle = document.createElement('h2');
    sectionTitle.style.marginTop = '24px';
    sectionTitle.style.marginBottom = '12px';
    sectionTitle.style.lineHeight = '1.4';
    sectionTitle.textContent = 'Summary';
    container.appendChild(sectionTitle);
    const sectionContent = document.createElement('div');
    sectionContent.className = 'pdf-section-content';
    sectionContent.style.marginBottom = '16px';
    sectionContent.style.lineHeight = '1.8';
    sectionContent.innerHTML = markdownToHtml(raw.content);
    container.appendChild(sectionContent);
  }

  if (interactionsData != null) {
    const sectionTitle = document.createElement('h2');
    sectionTitle.style.marginTop = '24px';
    sectionTitle.style.marginBottom = '12px';
    sectionTitle.style.lineHeight = '1.4';
    sectionTitle.textContent = 'All Interactions';
    container.appendChild(sectionTitle);
    const sectionContent = document.createElement('div');
    sectionContent.className = 'pdf-section-content';
    sectionContent.style.marginBottom = '16px';
    sectionContent.style.lineHeight = '1.8';
    sectionContent.innerHTML = markdownToHtml(formatAllInteractionsToMarkdown(interactionsData));
    container.appendChild(sectionContent);
  }

  const style = document.createElement('style');
  style.textContent = `
    @page { margin: 15mm 15mm 15mm 0mm; size: A4; }
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    h1 { page-break-before: always; page-break-after: avoid; page-break-inside: avoid; margin-top: 0; padding-top: 20px; font-size: 20px; font-weight: bold; color: #2d3748; }
    h1:first-child, h1:first-of-type { page-break-before: auto; margin-top: 0; padding-top: 0; }
    h2, h3, h4, h5, h6 { page-break-after: avoid; page-break-inside: avoid; margin-top: 24px; margin-bottom: 12px; font-weight: 600; color: #2d3748; }
    h2 { font-size: 18px; }
    h3 { font-size: 16px; }
    h4, h5, h6 { font-size: 14px; }
    .pdf-section-content p { margin-bottom: 12px; line-height: 1.8; orphans: 2; widows: 2; page-break-inside: avoid; }
    .pdf-section-content table { width: 100%; border-collapse: collapse; page-break-inside: auto; margin: 16px 0; font-size: 12px; }
    .pdf-section-content tr { page-break-inside: avoid; }
    .pdf-section-content th { border: 1px solid #e2e8f0; background: #f7fafc; padding: 10px 12px; text-align: left; font-weight: 600; color: #2d3748; }
    .pdf-section-content td { border: 1px solid #e2e8f0; padding: 10px 12px; }
    p { margin-bottom: 12px; line-height: 1.8; orphans: 2; widows: 2; page-break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; page-break-inside: auto; margin: 16px 0; }
    tr { page-break-inside: avoid; }
    th { border: 1px solid #e2e8f0; background: #f7fafc; padding: 8px; text-align: left; font-weight: 600; }
    td { border: 1px solid #e2e8f0; padding: 8px; }
    ul, ol { margin-bottom: 16px; }
    li { line-height: 1.7; margin-bottom: 6px; page-break-inside: avoid; }
    span, strong, em, b, i { page-break-inside: avoid; }
    div { page-break-inside: avoid; }
    blockquote { page-break-inside: avoid; margin: 16px 0; padding-left: 16px; border-left: 4px solid #e2e8f0; }
    code { page-break-inside: avoid; }
    pre { page-break-inside: avoid; margin: 16px 0; }
  `;
  container.appendChild(style);
  return container;
}

/** PDF options for html2pdf (used by download and by background upload). Exported for BackgroundPdfUploadTrigger. */
export function getPdfOptionsForMeeting(meeting: { client?: string } | null) {
  return {
    margin: [15, 5, 15, 15] as [number, number, number, number],
    filename: `Meeting_Report_${meeting?.client || 'untitled'}_${new Date().toISOString().split('T')[0]}.pdf`,
    image: { type: 'jpeg' as const, quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, letterRendering: true, allowTaint: false },
    jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const }
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Convert markdown to HTML for PDF section body (headings, tables, paragraphs). Returns safe HTML. */
function markdownToHtml(md: string): string {
  if (!md || typeof md !== 'string') return '';
  const normalized = md.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  if (!normalized) return '';
  try {
    const out = marked.parse(normalized, { async: false });
    return typeof out === 'string' ? out : normalized;
  } catch {
    return escapeHtml(normalized).replace(/\n/g, '<br/>');
  }
}

// Function to return markdown content directly from API
// const getFormattedMarkdownContent = (data: GeminiContent | undefined | null, sectionName: string, loading: boolean, error: ApiError | null): string => {  
//   if (loading) return "Loading...";
//   if (error && error.section === sectionName) return `Error loading ${sectionName}: ${error.message}`;
//   if (!data || !data.content) return `No data available for ${sectionName}.`;
//   return data.content;
// };

const getFormattedMarkdownContent = (
  data: CombinedInteractions | GeminiContent | undefined | null,
  sectionName: string,
  loading: boolean,
  error: ApiError | null
): string => {
  if (loading) return "Loading...";
  if (error && error.section === sectionName) return `Error loading ${sectionName}: ${error.message}`;
  if (!data) return `No data available for ${sectionName}.`;

  const highlightLine = (line: string): string => {
    const lowerLine = line.toLowerCase();
    if (lowerLine.includes('very important')) {
      // Highlight the entire line when "very important" is found
      return `<span style="display: block; padding: 4px 8px; border-left: 4px solid rgb(50, 134, 29); margin: 2px 0; border-radius: 4px; color: rgb(50, 134, 29); font-weight: 500; font-size:15px; ">${line}</span>`;
    }
    return line;
  };


  if ('content' in data && data.content) {
    // Clean any API errors from the content using the utility function
    const { content: cleanedContent } = cleanContentErrors(data.content, true);

    return cleanedContent
      .split('\n')
      .map(line => highlightLine(line))
      .join('\n');
  }

  try {
    // Helper function to format duration from seconds to "Xm Ys"
    const formatDuration = (seconds: number): string => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    };

    // Helper function to format ISO date to MM/DD/YYYY, HH:MM:SS AM/PM in IST
    const formatDateToIST = (isoDate: string): string => {
      try {
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString('en-US', {
          timeZone: 'Asia/Kolkata',
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }).replace(',', '');
      } catch {
        return 'N/A';
      }
    };

    let output = '';

    // Handle All Interactions
    let allInteractions: Array<{
      srNo: number;
      type: string;
      startTime: string;
      duration: string;
      status: string;
    }> = [];

    if ('allInteractions' in data && 'journey' in data.allInteractions && data.allInteractions.journey && Array.isArray(data.allInteractions.journey.sessions)) {
      allInteractions = data.allInteractions.journey.sessions.flatMap((session, sessionIndex) =>
        (session.interactions || []).map((item, index) => ({
          srNo: sessionIndex * (session.interactions?.length || 0) + index + 1,
          type: item.type || 'Unknown',
          startTime: formatDateToIST(item.startTime || ''),
          duration: item.timeSpent != null ? formatDuration(item.timeSpent) : 'N/A',
          status: 'complete'
        }))
      );
    } else if ('journey' in data && data.journey && Array.isArray(data.journey.sessions)) {
      allInteractions = data.journey.sessions.flatMap((session, sessionIndex) =>
        (session.interactions || []).map((item, index) => ({
          srNo: sessionIndex * (session.interactions?.length || 0) + index + 1,
          type: item.type || 'Unknown',
          startTime: formatDateToIST(item.startTime || ''),
          duration: item.timeSpent != null ? formatDuration(item.timeSpent) : 'N/A',
          status: 'complete'
        }))
      );
    } else if ('allInteractions' in data && 'error' in data.allInteractions) {
      // Clean the error message before displaying using utility function
      const errorMessage = cleanApiErrorMessage(
        String(data.allInteractions.error),
        'Error loading interactions'
      );

      allInteractions.push({
        srNo: 1,
        type: 'Error',
        startTime: 'N/A',
        duration: 'N/A',
        status: errorMessage
      });
    }

    // Generate All Interactions table
    if (allInteractions.length > 0) {
      const tableHeader = `| Sr.No. | Type | Start Time | Duration | Status |\n|--------|------|------------|----------|--------|\n`;
      const tableRows = allInteractions
        .map(
          (item, i) => {
            const row = `| ${i + 1} | ${item.type} | ${item.startTime} | ${item.duration} | ${item.status} |`;
            return highlightLine(row);
          }
        )
        .join('\n');
      output += `## Covered Interactions: ${allInteractions.length}\n\n${tableHeader}${tableRows}\n\n`;
    } else {
      const noDataLine = `## Covered Interactions: 0\n\nNo interactions available.\n\n`;
      output += highlightLine(noDataLine);
    }

    // Handle Missed Interactions
    if ('allInteractions' in data && 'missedInteractions' in data && 'data' in data.missedInteractions && Array.isArray(data.missedInteractions.data)) {
      // Get types from allInteractions
      const allInteractionTypes = new Set(allInteractions.map((item) => item.type.toUpperCase()));

      // Filter missedInteractions to include only types not in allInteractions
      const missedInteractions = data.missedInteractions.data
        .filter((type) => !allInteractionTypes.has(type.toUpperCase()))
        .map((type, index) => ({
          srNo: index + 1,
          type,
          startTime: '---',
          duration: '---',
          status: 'pending'
        }));

      // Generate Missed Interactions table
      if (missedInteractions.length > 0) {
        const tableHeader = `| Sr.No. | Type | Start Time | Duration | Status |\n|--------|------|------------|----------|--------|\n`;
        const tableRows = missedInteractions
          .map(
            (item, i) => {
              const row = `| ${i + 1} | ${item.type} | ${item.startTime} | ${item.duration} | ${item.status} |`;
              return highlightLine(row);
            }
          )
          .join('\n');
        output += `## Missed Interactions: ${missedInteractions.length}\n\n${tableHeader}${tableRows}`;
      } else {
        const noDataLine = `## Missed Interactions: 0\n\nNo interactions available.`;
        output += highlightLine(noDataLine);
      }
    } else if ('allInteractions' in data && 'missedInteractions' in data && 'error' in data.missedInteractions) {
      // Clean the error message before displaying using utility function
      const errorMessage = cleanApiErrorMessage(
        String(data.missedInteractions.error),
        'processing error'
      );

      const errorLine = `## Missed Interactions: 0\n\n⚠️ Error loading missed interactions: ${errorMessage}. Please try refreshing the page.`;
      output += highlightLine(errorLine);
    } else {
      const noDataLine = `## Missed Interactions: 0\n\nNo interactions data available.`;
      output += highlightLine(noDataLine);
    }

    return output;
  } catch (e) {
    const errorLine = `Unable to format data for ${sectionName}: ${(e as Error).message}`;
    return highlightLine(errorLine);
  }
};

const MeetingDetailsPage: React.FC = () => {
  const { meetingId: encodedMeetingId } = useParams<{ meetingId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const conversationIdFromUrl = searchParams.get('conversation_id');
  const meetingId = encodedMeetingId ? decodeURIComponent(encodedMeetingId) : undefined;
  const { meetings, currentMeeting, setCurrentMeeting, addMeetingToContext } = useMeetingContext();

  // Seed a meeting passed via navigation state (e.g. from the sales-executive
  // list, whose meetings are scoped by salesExecutiveId and therefore aren't part
  // of the shared global meeting context). This lets the page resolve the meeting
  // directly instead of falling back to paging through the global meetings feed.
  useEffect(() => {
    const stateMeeting = (location.state as { meeting?: MeetingFromService } | null)?.meeting;
    if (stateMeeting && stateMeeting.meeting_id === meetingId) {
      const inContext = meetings?.find(m => m.meeting_id === meetingId);
      if (!inContext) {
        addMeetingToContext(stateMeeting);
      }
    }
  }, [location.state, meetingId, meetings, addMeetingToContext]);

  // Local state to store the meeting if we fetch it via fallback API call
  const [fallbackMeeting, setFallbackMeeting] = useState<any>(null);
  const [isFetchingMeeting, setIsFetchingMeeting] = useState(false);
  const [activeSection, setActiveSection] = useState<BaseSectionType>("Meeting Summary");
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);

  // State for API data
  const [fullAnalysisData, setFullAnalysisData] = useState<GeminiContent | null>(null);
  const [allInteractionData, setAllInteractionData] = useState<GeminiContent | null>(null);
  const [interactionCount, setInteractionCount] = useState<InteractionCounts>({ allInteractionsCount: 0, missedInteractionsCount: 0 });
  const [apiErrors, setApiErrors] = useState<ApiError[]>([]);
  const [isFetchingSummaries, setIsFetchingSummaries] = useState(false);
  // Tracks the slower All Interactions fetch separately so only that tab shows a
  // loader once the (faster) summary has already rendered the rest of the page.
  const [isFetchingInteractions, setIsFetchingInteractions] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  // The conversation id actually used for this meeting — also drives the audio player.
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  // NEW: Ref for the entire printable content area
  const printableContentRef = useRef<HTMLDivElement>(null);
  // Section refs for scroll navigation
  const sectionRefs = useRef<Record<BaseSectionType, HTMLDivElement | null>>({
    "Strategic Summary": null,
    "Meeting Summary": null,
    "Key Topics": null,
    "Action Items": null,
    "Participants": null,
    "Decisions Made": null,
    "Questions Raised": null,
    "Client's Snapshot": null,
    "Key Drivers & Risks": null,
    "Product Options": null,
    "Emotional Arc & Sentiment": null,
    "All Interactions": null
  });

  // Fallback function to search for meeting across multiple pages
  const findMeetingById = async (searchMeetingId: string) => {
    setIsFetchingMeeting(true);

    try {
      // Search through multiple pages to find the meeting
      for (let page = 1; page <= 5; page++) { // Search first 5 pages        
        const response = await meetingService.getMeetings({
          type: 'real_estate',
          page,
          limit: 10
        });

        if (response.data && Array.isArray(response.data)) {
          const foundMeeting = response.data.find(m => m.meeting_id === searchMeetingId);
          if (foundMeeting) {
            setFallbackMeeting(foundMeeting);

            // Add to context so MeetingHeaderDetails can access it
            addMeetingToContext(foundMeeting);

            if (setCurrentMeeting) {
              setCurrentMeeting(foundMeeting);
            }
            return foundMeeting;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('[MeetingDetailsPage] Error in API fallback search:', error);
      return null;
    } finally {
      setIsFetchingMeeting(false);
    }
  };

  // Effect for setting the current meeting in context
  useEffect(() => {
    if (meetingId && meetings && setCurrentMeeting) {
      const foundMeeting = meetings.find(m => m.meeting_id === meetingId);
      if (foundMeeting) {
        setCurrentMeeting(foundMeeting);
      }
    }
  }, [meetingId, meetings, setCurrentMeeting]);

  // Effect for checking processing status and fetching summaries
  useEffect(() => {
    if (!meetingId) {
      setPageError("No Meeting ID provided.");
      setIsLoadingPage(false);
      return;
    }

    // Find the current meeting to get conversation_id and request_id
    const meeting = meetings?.find(m => m.meeting_id === meetingId);

    if (!meeting) {
      setPageError("Meeting not found.");
      setIsLoadingPage(false);
      return;
    }

    // Check if meeting has conversation data
    // Use conversation_id from URL if provided, otherwise use first from array or the string value
    let conversationIdToUse: string | null = null;

    if (conversationIdFromUrl) {
      // Validate that the conversation_id from URL exists in the meeting's conversation_ids
      const meetingConversationIds = Array.isArray(meeting.conversation_id)
        ? meeting.conversation_id
        : meeting.conversation_id ? [meeting.conversation_id] : [];

      if (meetingConversationIds.includes(conversationIdFromUrl)) {
        conversationIdToUse = conversationIdFromUrl;
      } else {
        // If URL conversation_id doesn't match, fall back to latest (last index)
        conversationIdToUse = Array.isArray(meeting.conversation_id)
          ? (meeting.conversation_id.length > 0 ? meeting.conversation_id[meeting.conversation_id.length - 1] : null)
          : meeting.conversation_id;
      }
    } else {
      // No conversation_id in URL, use latest (last index) from array or the string value
      conversationIdToUse = Array.isArray(meeting.conversation_id)
        ? (meeting.conversation_id.length > 0 ? meeting.conversation_id[meeting.conversation_id.length - 1] : null)
        : meeting.conversation_id;
    }

    if (!conversationIdToUse) {
      setPageError("This meeting doesn't have conversation data associated with it.");
      setIsLoadingPage(false);
      return;
    }

    // If there's no request_id, we can't check status
    if (!meeting.request_id) {
      setPageError("This meeting doesn't have processing request information.");
      setIsLoadingPage(false);
      return;
    }

    setActiveConversationId(conversationIdToUse);
    setIsFetchingSummaries(true);
    setIsFetchingInteractions(true);
    setApiErrors([]);
    setIsLoadingPage(true);

    const checkStatusAndFetchData = async () => {
      // Get the meeting (either from context or fallback)
      let meeting = meetings?.find(m => m.meeting_id === meetingId);
      if (!meeting && fallbackMeeting) {
        meeting = fallbackMeeting;
      }

      if (!meeting) {
        setPageError("Meeting not found.");
        setIsLoadingPage(false);
        return;
      }

      // Check if meeting has conversation data
      // Use conversation_id from URL if provided, otherwise use first from array or the string value
      let conversationIdToUse: string | null = null;

      if (conversationIdFromUrl) {
        // Validate that the conversation_id from URL exists in the meeting's conversation_ids
        const meetingConversationIds = Array.isArray(meeting.conversation_id)
          ? meeting.conversation_id
          : meeting.conversation_id ? [meeting.conversation_id] : [];

        if (meetingConversationIds.includes(conversationIdFromUrl)) {
          conversationIdToUse = conversationIdFromUrl;
        } else {
          // If URL conversation_id doesn't match, fall back to first one
          conversationIdToUse = Array.isArray(meeting.conversation_id)
            ? (meeting.conversation_id.length > 0 ? meeting.conversation_id[0] : null)
            : meeting.conversation_id;
        }
      } else {
        // No conversation_id in URL, use latest (last index) from array or the string value
        conversationIdToUse = Array.isArray(meeting.conversation_id)
          ? (meeting.conversation_id.length > 0 ? meeting.conversation_id[meeting.conversation_id.length - 1] : null)
          : meeting.conversation_id;
      }

      if (!conversationIdToUse) {
        setPageError("This meeting doesn't have conversation data associated with it.");
        setIsLoadingPage(false);
        return;
      }

      // If there's no request_id, we can't check status
      if (!meeting.request_id) {
        setPageError("This meeting doesn't have processing request information.");
        setIsLoadingPage(false);
        return;
      }

      try {
        // First, check the processing status
        const statusResponse = await conversationService.getProcessingStatus(meeting.request_id!);
        const status = statusResponse.data?.status;

        setProcessingStatus(status);

        if (status === 'COMPLETED') {
          // Start the summary (main content) and the interactions data
          // (All Interactions tab) in parallel. The interactions endpoints
          // (customer journey + project interaction-list) are slow, so we do
          // NOT block the whole page on them: we render as soon as the summary
          // resolves and let the All Interactions tab fill in afterwards using
          // its own per-section loader.
          const summaryPromise = conversationService.getSummaryDocument(conversationIdToUse);
          const interactionsSettled: Promise<{ ok: boolean; value?: any; reason?: any }> = (
            meeting.clientId
              ? appAnalyticsService.getCombinedInteractions({ clientId: meeting.clientId, project: meeting.project })
              : Promise.reject(new Error("No client ID available"))
          ).then(
            (value) => ({ ok: true, value }),
            (reason) => ({ ok: false, reason })
          );

          const newApiErrors: ApiError[] = [];

          // --- Summary: awaited first so the page can render right away ---
          try {
            const summaryValue = await summaryPromise;
            setFullAnalysisData(summaryValue as GeminiContent);
          } catch (summaryError) {
            const errorMessage = cleanApiErrorMessage(
              (summaryError as Error).message,
              'Failed to load summary'
            );
            // Push error for all summary sections
            const summarySections: BaseSectionType[] = [
              "Strategic Summary",
              "Meeting Summary",
              "Key Topics",
              "Action Items",
              "Participants",
              "Decisions Made",
              "Questions Raised",
              "Client's Snapshot",
              "Key Drivers & Risks",
              "Product Options",
              "Emotional Arc & Sentiment"
            ];
            summarySections.forEach(section => {
              newApiErrors.push({ section, message: errorMessage });
            });
          }

          // Surface any summary errors and unblock the page now — the slow
          // interactions calls below no longer hold up the whole render. The
          // summary sections render immediately; only the All Interactions tab
          // keeps loading (via isFetchingInteractions).
          setApiErrors([...newApiErrors]);
          setIsFetchingSummaries(false);
          setIsLoadingPage(false);

          // --- Interactions: resolved after the page is already interactive ---
          const interactionsResult = await interactionsSettled;
          setIsFetchingInteractions(false);
          if (interactionsResult.ok) {
            const data = interactionsResult.value as any;
            setAllInteractionData(data as GeminiContent);

            // Compute interaction counts
            const counts: InteractionCounts = {
              allInteractionsCount: 0,
              missedInteractionsCount: 0,
            };
            if (
              'allInteractions' in data &&
              'journey' in data.allInteractions &&
              data.allInteractions.journey &&
              Array.isArray(data.allInteractions.journey.sessions)
            ) {
              counts.allInteractionsCount = data.allInteractions.journey.sessions
                .flatMap((session: Session) => session.interactions || [])
                .length;
            }
            if (
              'missedInteractions' in data &&
              'data' in data.missedInteractions &&
              Array.isArray(data.missedInteractions.data)
            ) {
              const allInteractionTypes = new Set(
                'journey' in data.allInteractions &&
                  data.allInteractions.journey &&
                  Array.isArray(data.allInteractions.journey.sessions)
                  ? data.allInteractions.journey.sessions
                    .flatMap((session: Session) => session.interactions || [])
                    .map((item: Interaction) => item.type?.toUpperCase())
                    .filter((type: string | undefined): type is string => !!type)
                  : []
              );
              counts.missedInteractionsCount = data.missedInteractions.data.filter(
                (type: string) => !allInteractionTypes.has(type.toUpperCase())
              ).length;
            }
            setInteractionCount(counts as InteractionCounts);
          } else {
            const errorMessage = cleanApiErrorMessage(
              (interactionsResult.reason as Error).message,
              'Failed to load interactions'
            );
            newApiErrors.push({ section: "All Interactions", message: errorMessage });
          }

          setApiErrors([...newApiErrors]);
          // Total sections is 11 (10 summary + 1 interaction)
          if (newApiErrors.length === 11) {
            setPageError("Failed to load any summary data.");
          } else {
            setPageError(null);
          }
        } else if (status === 'FAILED') {
          setPageError("The conversation processing failed. Please try reprocessing this conversation.");
        } else if (status === 'PENDING' || status === 'PROCESSING') {
          // Processing is still ongoing
          setPageError(`Conversation is still being processed. Current status: ${status}. Please check back later.`);
        } else {
          setPageError(`Unknown processing status: ${status}. Please contact support.`);
        }
      } catch (err) {
        console.error("[MeetingDetailsPage] Error in checkStatusAndFetchData:", err);
        setPageError("Failed to load conversation data. Please try again later.");
      } finally {
        setIsLoadingPage(false);
        setIsFetchingSummaries(false);
        setIsFetchingInteractions(false);
      }
    };

    checkStatusAndFetchData();

  }, [meetingId, meetings, fallbackMeeting, conversationIdFromUrl]);

  // Separate effect to handle fallback API call when meeting not found in context
  useEffect(() => {
    const searchForMeeting = async () => {
      if (!meetingId) return;

      // Only search if meeting is not found in context and we haven't already found it
      const foundInContext = meetings?.find(m => m.meeting_id === meetingId);
      if (foundInContext || fallbackMeeting || isFetchingMeeting) return;

      await findMeetingById(meetingId);
    };

    searchForMeeting();
  }, [meetingId, meetings, fallbackMeeting, isFetchingMeeting]);

  const getContentForSection = (section: BaseSectionType): ParsedContent => {
    let data: { content?: string } | GeminiContent | null | undefined;
    const errorForSection = apiErrors.find(e => e.section === section) || null;

    // Direct mapping for summary sections
    switch (section) {
      case "Strategic Summary":
      case "Meeting Summary":
      case "Participants":
      case "Decisions Made":
      case "Client's Snapshot":
      case "Key Drivers & Risks":
      case "Product Options":
      case "Emotional Arc & Sentiment":
        const key = apiSectionKeyMap[section];
        if (fullAnalysisData?.summary?.sections?.[key]) {
          data = { content: fullAnalysisData.summary.sections[key] };
        }
        break;

      case "Action Items":
        if (fullAnalysisData?.summary?.sections?.action_items) {
          try {
            const rawActionItems = fullAnalysisData.summary.sections.action_items;
            let items = [];
            try {
              // Try to parse if string, otherwise assume it might be array from API
              if (typeof rawActionItems === 'string') {
                items = JSON.parse(rawActionItems);
              } else if (Array.isArray(rawActionItems)) {
                items = rawActionItems;
              }
            } catch (e) {
              console.warn("Failed to parse action_items", e);
              // If it's a simple string that's not JSON, use it directly
              if (typeof rawActionItems === 'string') {
                data = { content: rawActionItems };
                break;
              }
            }

            if (Array.isArray(items) && items.length > 0) {
              const formattedItems = items.map((item: any) => {
                // Check if item is object or string
                if (typeof item === 'string') return `- ${item}`;
                return `- ${item.action || item.item || JSON.stringify(item)}`;
              }).join('\n');
              data = { content: formattedItems };
            } else if (Array.isArray(items) && items.length === 0) {
              data = { content: "No action items identified." };
            }
            else {
              data = { content: String(rawActionItems) };
            }
          } catch (e) {
            data = { content: "Error parsing Action Items." };
          }
        }
        break;

      case "Key Topics":
        if (fullAnalysisData?.summary?.sections?.key_topics) {
          try {
            // Parse JSON string if needed, or use directly if it's already a string
            const rawTopics = fullAnalysisData.summary.sections.key_topics;
            let topics = [];
            try {
              topics = JSON.parse(rawTopics);
            } catch (e) {
              // If not JSON, treat as regular string or maybe it's already an object
              console.warn("Failed to parse key_topics JSON", e);
              data = { content: rawTopics }; // Fallback
              break;
            }

            if (Array.isArray(topics)) {
              const formattedTopics = topics.map((t: any) => {
                return `### ${t.topic}\n${t.description}\n*Timestamp: ${t.timestamp}*`;
              }).join('\n\n');
              data = { content: formattedTopics };
            } else {
              data = { content: String(rawTopics) };
            }
          } catch (e) {
            data = { content: "Error parsing Key Topics data." };
          }
        }
        break;

      case "Questions Raised":
        if (fullAnalysisData?.summary?.sections?.questions_raised) {
          try {
            const rawQuestions = fullAnalysisData.summary.sections.questions_raised;
            let questions = [];
            try {
              questions = JSON.parse(rawQuestions);
            } catch (e) {
              console.warn("Failed to parse questions_raised JSON", e);
              data = { content: rawQuestions }; // Fallback
              break;
            }

            if (Array.isArray(questions) && questions.length > 0) {
              const formattedQuestions = questions.map((q: any) => `- ${q}`).join('\n');
              data = { content: formattedQuestions };
            } else if (Array.isArray(questions) && questions.length === 0) {
              data = { content: "No questions raised were identified in this meeting." };
            }
            else {
              data = { content: String(rawQuestions) };
            }
          } catch (e) {
            data = { content: "Error parsing Questions Raised data." };
          }
        }
        break;

      case "All Interactions": data = allInteractionData; break;
    }
    const rawContent = getFormattedMarkdownContent(data, section, isFetchingSummaries, errorForSection);
    return parseContentAndQuotes(rawContent, section);
  };

  // Sections to display in the Tabs based on API response
  const availableSections = useMemo(() => {
    const sections: BaseSectionType[] = [];

    if (!fullAnalysisData?.summary?.sections) {
      // Default fallback if no data yet (or stick to empty)
      return ["All Interactions"] as BaseSectionType[];
    }

    const { sections: apiSections } = fullAnalysisData.summary;

    if (apiSections.strategic_summary) sections.push("Strategic Summary");
    if (apiSections.meeting_summary) sections.push("Meeting Summary");

    // Check key_topics (JSON string)
    if (apiSections.key_topics && apiSections.key_topics !== '[]') sections.push("Key Topics");

    // Check action_items (JSON string or array)
    if (apiSections.action_items && apiSections.action_items !== '[]') sections.push("Action Items");

    if (apiSections.participants) sections.push("Participants");
    if (apiSections.decisions_made) sections.push("Decisions Made");

    // Check questions_raised (JSON string)
    if (apiSections.questions_raised && apiSections.questions_raised !== '[]') sections.push("Questions Raised");

    if (apiSections.client_snapshot) sections.push("Client's Snapshot");
    if (apiSections.key_drivers_risk) sections.push("Key Drivers & Risks");
    if (apiSections.product_options) sections.push("Product Options");
    if (apiSections.emotional_arc) sections.push("Emotional Arc & Sentiment");

    // Always add All Interactions
    sections.push("All Interactions");

    return sections;
  }, [fullAnalysisData]);

  // Update active section if current one becomes unavailable
  useEffect(() => {
    if (availableSections.length > 0 && !availableSections.includes(activeSection)) {
      setActiveSection(availableSections[0]);
    }
  }, [availableSections, activeSection]);

  const [localValidSections, setLocalValidSections] = useState<BaseSectionType[]>([]);
  useEffect(() => {
    setLocalValidSections(availableSections);
  }, [availableSections]);

  // Scroll to section when tab is clicked
  const handleSectionChange = useCallback((sectionCandidate: string) => {
    if (localValidSections.includes(sectionCandidate as BaseSectionType)) {
      const section = sectionCandidate as BaseSectionType;
      const sectionElement = sectionRefs.current[section];
      if (sectionElement && contentRef.current) {
        const headerHeight = headerRef.current?.offsetHeight || 0;
        const elementTop = sectionElement.offsetTop;
        const scrollTop = elementTop - headerHeight - 20; // 20px padding

        contentRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
      setActiveSection(section);
    } else {
      console.warn(`MeetingDetailsPage: Received unhandled section "${sectionCandidate}" from header.`);
    }
  }, [localValidSections]);

  // Scroll spy to update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const headerHeight = headerRef.current?.offsetHeight || 0;
      const scrollTop = contentRef.current.scrollTop + headerHeight + 50; // 50px offset for better UX

      // Find which section is currently in view
      for (const section of localValidSections) {
        const element = sectionRefs.current[section];
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;

          if (scrollTop >= elementTop && scrollTop < elementBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [localValidSections]);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.scrollY > headerRef.current.offsetTop + headerRef.current.offsetHeight) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getAnimationDelay = (index: number) => 0.1 + (index * 0.1);

  const renderSection = (section: BaseSectionType, index: number) => {
    const sectionTitle = sectionNameMapping[section] || section;

    if (!sectionTitle) return null;

    return (
      <Tab
        key={section}
        onClick={() => handleSectionChange(section)}
        animation={`fadeInUp 0.5s ease-in-out ${getAnimationDelay(index)}s forwards`}
        opacity={0}
      >
        {sectionTitle}
      </Tab>
    );
  };


  // Render individual section content
  const renderSectionContent = (section: BaseSectionType) => {
    const { segments } = getContentForSection(section);
    let quoteIndex = 0;
    // All Interactions loads on its own (slower) timeline; every other section
    // follows the summary fetch.
    const sectionIsFetching = section === "All Interactions" ? isFetchingInteractions : isFetchingSummaries;

    return (
      <Box
        key={section}
        ref={(el) => (sectionRefs.current[section] = el)}
        mb={8}
        id={section.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}
      >
        <ExpandableSection
          title={sectionNameMapping[section]}
          content={
            sectionIsFetching && !apiErrors.find(e => e.section === section) && !isLoadingPage ? (
              <Flex justify="center" align="center" height="100px">
                <CircularProgress isIndeterminate size="sm" />
                <Text ml={2}>Loading {sectionNameMapping[section]}...</Text>
              </Flex>
            ) : apiErrors.find(e => e.section === section) ? (
              <Alert status="error">
                <AlertIcon />
                Error loading {sectionNameMapping[section]}: {cleanApiErrorMessage(
                  apiErrors.find(e => e.section === section)?.message || '',
                  'Unable to load this section'
                )}
              </Alert>
            ) : (
              <Box>
                {segments.map((segment, index) => {
                  if (segment.type === 'content') {
                    return (
                      <Box
                        key={index}
                        sx={{
                          '& h1': { fontSize: 'xl', fontWeight: 'bold', mb: 4 },
                          '& h2': { fontSize: 'lg', fontWeight: 'semibold', mb: 3 },
                          '& h3': { fontSize: 'md', fontWeight: 'medium', mb: 2 },
                          '& p': { mb: 2, lineHeight: '1.6' },
                          '& ul': { pl: 4, mb: 2 },
                          '& ol': { pl: 4, mb: 2 },
                          '& li': { mb: 1 },
                          '& strong': { fontWeight: 'bold' },
                          '& em': { fontStyle: 'italic' },
                          '& code': {
                            bg: 'gray.100',
                            px: 1,
                            py: 0.5,
                            borderRadius: 'sm',
                            fontSize: 'sm'
                          },
                          '& pre': {
                            bg: 'gray.100',
                            p: 3,
                            borderRadius: 'md',
                            overflowX: 'auto',
                            mb: 3
                          },
                          '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'blue.200',
                            pl: 4,
                            py: 2,
                            bg: 'blue.50',
                            mb: 3
                          },
                          '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            mb: 3
                          },
                          '& th': {
                            border: '1px solid',
                            borderColor: 'gray.300',
                            bg: 'gray.100',
                            p: 2,
                            textAlign: 'left',
                            fontWeight: 'semibold'
                          },
                          '& td': {
                            border: '1px solid',
                            borderColor: 'gray.300',
                            p: 2
                          }
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizeSchema]]}
                        >
                          {segment.content}
                        </ReactMarkdown>
                      </Box>
                    );
                  } else if (segment.type === 'quote') {
                    const currentQuoteIndex = quoteIndex;
                    quoteIndex++;
                    return (
                      <CollapsibleQuote
                        key={`quote-${index}`}
                        quote={segment.content}
                        index={currentQuoteIndex}
                      />
                    );
                  } else if (segment.type === 'subsection') {
                    const currentQuoteIndex = quoteIndex;
                    quoteIndex++;
                    return (
                      <CollapsibleQuote
                        key={`subsection-${index}`}
                        quote={segment.content}
                        index={currentQuoteIndex}
                        title={segment.subsectionTitle}
                      />
                    );
                  }
                  return null;
                })}
              </Box>
            )
          }
        />
      </Box>
    );
  };

  // Build PDF container from current printable content (for manual download only)
  const buildPdfContainer = useCallback((meeting: { name?: string; date?: string; project?: string; client?: string } | null): HTMLElement | null => {
    if (!printableContentRef.current) return null;
    const pdfContainer = document.createElement('div');
    pdfContainer.style.width = '210mm';
    pdfContainer.style.maxWidth = '210mm';
    pdfContainer.style.margin = '0';
    pdfContainer.style.padding = '20mm';
    pdfContainer.style.boxSizing = 'border-box';
    pdfContainer.style.fontFamily = 'Arial, sans-serif';
    pdfContainer.style.fontSize = '12px';
    pdfContainer.style.lineHeight = '1.4';
    pdfContainer.style.color = '#333';
    pdfContainer.style.backgroundColor = 'white';

    const header = document.createElement('div');
    header.style.textAlign = 'center';
    header.style.marginBottom = '30px';
    header.style.paddingBottom = '20px';
    header.style.borderBottom = '2px solid #e2e8f0';
    header.innerHTML = `
      <h1 style="margin: 0 0 10px 0; font-size: 20px; font-weight: bold; color: #2d3748;">
        Meeting: ${meeting?.name || 'Untitled'}
      </h1>
      <p style="margin: 5px 0; font-size: 14px; color: #718096;">
        Date: ${meeting?.date || 'N/A'}
      </p>
      <p style="margin: 5px 0; font-size: 14px; color: #718096;">
        Project: ${meeting?.project || 'N/A'}
      </p>
    `;

    const contentClone = printableContentRef.current.cloneNode(true) as HTMLElement;
    const paragraphs = contentClone.querySelectorAll('p');
    paragraphs.forEach(p => { (p as HTMLElement).style.lineHeight = '1.8'; });
    const listItems = contentClone.querySelectorAll('li');
    listItems.forEach(item => { (item as HTMLElement).style.lineHeight = '1.7'; });
    const headings = contentClone.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(h => { (h as HTMLElement).style.lineHeight = '1.4'; });
    contentClone.querySelectorAll('.chakra-collapse').forEach(el => { (el as HTMLElement).style.display = 'none'; });
    contentClone.querySelectorAll('[data-testid="collapse"]').forEach(el => { (el as HTMLElement).style.display = 'none'; });

    pdfContainer.appendChild(header);
    pdfContainer.appendChild(contentClone);

    const style = document.createElement('style');
    style.textContent = `
      @page { margin: 15mm 15mm 15mm 0mm; size: A4; }
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
      h1 { page-break-before: always; page-break-after: avoid; page-break-inside: avoid; margin-top: 0; padding-top: 20px; }
      h1:first-child, h1:first-of-type { page-break-before: auto; margin-top: 0; padding-top: 0; }
      h2, h3, h4, h5, h6 { page-break-after: avoid; page-break-inside: avoid; margin-top: 24px; margin-bottom: 12px; }
      p { margin-bottom: 12px; line-height: 1.8; orphans: 2; widows: 2; page-break-inside: avoid; }
      table { page-break-inside: auto; margin: 16px 0; }
      tr { page-break-inside: avoid; }
      ul, ol { margin-bottom: 16px; }
      li { line-height: 1.7; margin-bottom: 6px; page-break-inside: avoid; }
      span, strong, em, b, i { page-break-inside: avoid; }
      div { page-break-inside: avoid; }
      blockquote { page-break-inside: avoid; margin: 16px 0; padding-left: 16px; border-left: 4px solid #e2e8f0; }
      code { page-break-inside: avoid; }
      pre { page-break-inside: avoid; margin: 16px 0; }
    `;
    pdfContainer.appendChild(style);
    return pdfContainer;
  }, []);

  // Manual PDF download (unchanged behavior; does not trigger upload)
  const handleDownloadReport = async () => {
    const meeting = meetings?.find(m => m.meeting_id === meetingId) || null;
    if (!printableContentRef.current) {
      console.warn("Printable content ref is not available.");
      return;
    }
    const pdfContainer = buildPdfContainer(meeting);
    if (!pdfContainer) return;
    try {
      const options = getPdfOptionsForMeeting(meeting);
      await html2pdf().set(options).from(pdfContainer).save();
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please check the console for details and try again.");
    }
  };

  if (isLoadingPage || isFetchingMeeting) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <CircularProgress isIndeterminate color="blue.500" />
        <Text mt={4}>
          {isFetchingMeeting ? 'Searching for meeting...' : 'Loading meeting details...'}
        </Text>
      </Flex>
    );
  }


  if (pageError && !isFetchingSummaries) {
    // Don't show error if we have any summary data
    const hasAnyData = !!(fullAnalysisData || allInteractionData);
    if (hasAnyData) {
      console.log('[MeetingDetailsPage] Have data despite pageError, proceeding to render content');
    } else {
      return (
        <Flex justify="center" align="center" height="100vh" direction="column">
          <Alert status="warning" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px" >
            <AlertIcon boxSize="40px" mr={0} />
            <Text fontSize="xl" mt={4} mb={2} fontWeight="bold">
              {processingStatus === 'PENDING' || processingStatus === 'PROCESSING' ?
                'Conversation Processing In Progress' :
                'Error Loading Meeting Summaries'
              }
            </Text>
            <Text>{pageError}</Text>
            {(processingStatus === 'PENDING' || processingStatus === 'PROCESSING') && (
              <Text fontSize="sm" mt={2} color="gray.600">
                Processing usually takes 2-5 minutes. You can refresh this page to check for updates.
              </Text>
            )}
          </Alert>
        </Flex>
      );
    }
  }

  if (!currentMeeting && !isLoadingPage && !isFetchingSummaries) {
    return (
      <Flex justify="center" align="center" height="calc(100vh - 200px)">
        <Text fontSize="lg" color="gray.500">
          Meeting details could not be loaded, or conversation processing is pending.
        </Text>
      </Flex>
    );
  }

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      <Box ref={headerRef} zIndex="sticky" top="0" bg="white" padding={4} boxShadow={isScrolled ? "md" : "none"} transition="box-shadow 0.2s">
        <MeetingHeaderDetails
          activeSection={activeSection}
          availableSections={availableSections}
          interactionCount={interactionCount}
          onSectionChange={handleSectionChange}
          onDownloadReport={() => Promise.resolve(handleDownloadReport())}
          conversationId={activeConversationId}
        />
      </Box>

      <Box ref={contentRef} flex="1" p={4} overflowY="auto">
        {/* Display general API errors for summaries if any */}
        {apiErrors.length > 0 && apiErrors.length < availableSections.length && (
          <Alert status="warning" mb={4}>
            <AlertIcon />
            Some summary sections may have failed to load. Please check individual tabs or try refreshing.
          </Alert>
        )}

        {/* Render all sections in order */}
        <Fade in={true}>
          <Box ref={printableContentRef}>
            {availableSections.map(section => renderSectionContent(section))}
          </Box>
        </Fade>


        {/* Loading state for when fetching summaries */}
        {isFetchingSummaries && (
          <Flex justify="center" align="center" height="200px">
            <CircularProgress isIndeterminate color="blue.500" />
            <Text ml={2}>Loading summaries...</Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default MeetingDetailsPage;